/**
 * Le sommaire d'un cahier, dans l'ordre du livre.
 *
 * L'ordre est celui de l'imprimé et non celui du référentiel : le parent a le
 * cahier sous la main, et un sommaire qui ne suit pas la table des matières
 * papier oblige à chercher chaque chapitre deux fois. Il vient de la table
 * publiée avec le découpage (`components/exercise/data/ouvrages/`), la seule
 * chose qui porte les titres et les pages — les clés en base sont « 5e-11 »,
 * « IV », « 4 », et ne se lisent pas.
 *
 * Le compte de ce qui est déjà fait accompagne chaque ligne sans rien barrer :
 * refaire un chapitre est une décision de parent, pas une erreur à empêcher.
 *
 * **Un cahier de cycle ne montre que l'année de l'enfant** (Hedi, 25 septembre
 * 2026), et l'écran le dit. La compil' de maths en tient 102, dont 25 de 5ᵉ :
 * un sommaire qui raccourcit sans un mot se lit comme un découpage raté, et
 * envoie chercher une panne là où il n'y a qu'un livre ouvert à la bonne page.
 */

import { Link, useParams } from "react-router-dom";

import { niveauEnClair } from "@/ui/cahiers";
import { useContenuDOuvrage } from "@/ui/enCache";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { entrainementsDe } from "@/v2/chemins";

export function ChapitresScreen() {
  const { childId = "", ouvrage = "" } = useParams();
  // Le sommaire d'un livre acheté est fixe : gardé dans le navigateur le temps
  // de la session, le retour depuis un chapitre est immédiat (`ui/enCache`).
  const { donnees: sommaire, erreur } = useContenuDOuvrage("parascolaireChapitres", {
    params: { child_id: childId, ouvrage },
  });

  const chapitres = sommaire?.chapitres ?? [];

  return (
    <Screen title={sommaire?.ouvrage_titre ?? "Le cahier"}>
      <p className="notice">
        <Link to={entrainementsDe(childId)}>← Tous les cahiers</Link>
      </p>

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!sommaire && !erreur ? <Loading /> : null}

      {/* Dite une fois, en tête : la répéter sous chaque ligne ferait lire une
          alerte là où il n'y a qu'un périmètre. */}
      {sommaire?.niveau ? (
        <p className="muted small">
          Ce cahier court sur plusieurs années : seuls les chapitres de{" "}
          {niveauEnClair(sommaire.niveau)} sont affichés.
        </p>
      ) : null}

      {sommaire && chapitres.length === 0 ? (
        <Card>
          <Empty>Ce cahier n'a aucun chapitre servable pour cet enfant.</Empty>
        </Card>
      ) : null}

      {chapitres.length > 0 ? (
        <Card>
          <ul className="list">
            {chapitres.map((chapitre) => (
              <li key={chapitre.cle} className="list__item">
                <div>
                  <Link to={`${entrainementsDe(childId)}/${ouvrage}/${encodeURIComponent(chapitre.cle)}`}>
                    {chapitre.titre}
                  </Link>
                  <p className="muted small">
                    {chapitre.page ? `page ${chapitre.page} — ` : ""}
                    {chapitre.exercices} exercices
                    {chapitre.faits > 0 ? `, dont ${chapitre.faits} déjà faits` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </Screen>
  );
}
